import { useState, useEffect, useCallback } from 'react';
import webSocketService from '../services/websocket';

export function useWebSocket() {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleConnected = () => {
      setConnectionStatus('connected');
      setError(null);
    };

    const handleDisconnected = (reason) => {
      setConnectionStatus('disconnected');
      console.log('WebSocket disconnected:', reason);
    };

    const handleError = (error) => {
      setConnectionStatus('error');
      setError(error);
    };

    const handleMessage = (data) => {
      setLastMessage({
        ...data,
        timestamp: new Date()
      });
    };

    // Set up event listeners
    webSocketService.on('connected', handleConnected);
    webSocketService.on('disconnected', handleDisconnected);
    webSocketService.on('error', handleError);
    webSocketService.on('welcome', handleMessage);
    webSocketService.on('metrics_update', handleMessage);
    webSocketService.on('alerts_update', handleMessage);
    webSocketService.on('new_call', handleMessage);
    webSocketService.on('call_ended', handleMessage);
    webSocketService.on('new_alert', handleMessage);
    webSocketService.on('overview_update', handleMessage);

    // Initial status
    setConnectionStatus(webSocketService.getConnectionStatus());

    return () => {
      // Clean up listeners
      webSocketService.off('connected', handleConnected);
      webSocketService.off('disconnected', handleDisconnected);
      webSocketService.off('error', handleError);
      webSocketService.off('welcome', handleMessage);
      webSocketService.off('metrics_update', handleMessage);
      webSocketService.off('alerts_update', handleMessage);
      webSocketService.off('new_call', handleMessage);
      webSocketService.off('call_ended', handleMessage);
      webSocketService.off('new_alert', handleMessage);
      webSocketService.off('overview_update', handleMessage);
    };
  }, []);

  const subscribe = useCallback((feeds) => {
    webSocketService.subscribe(feeds);
  }, []);

  const unsubscribe = useCallback((feeds) => {
    webSocketService.unsubscribe(feeds);
  }, []);

  const requestMetrics = useCallback(() => {
    webSocketService.getMetrics();
  }, []);

  const requestAlerts = useCallback(() => {
    webSocketService.getAlerts();
  }, []);

  return {
    connectionStatus,
    lastMessage,
    error,
    isConnected: connectionStatus === 'connected',
    subscribe,
    unsubscribe,
    requestMetrics,
    requestAlerts
  };
}

export function useWebSocketData(eventType, initialData = null) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleData = (eventData) => {
      if (eventData.type === eventType) {
        setData(eventData.data);
        setLoading(false);
        setError(null);
      }
    };

    const handleError = (error) => {
      setError(error);
      setLoading(false);
    };

    // Set up listeners
    webSocketService.on(eventType, handleData);
    webSocketService.on('error', handleError);

    // Request initial data if needed
    if (eventType === 'metrics_update') {
      webSocketService.getMetrics();
    } else if (eventType === 'alerts_update') {
      webSocketService.getAlerts();
    }

    return () => {
      webSocketService.off(eventType, handleData);
      webSocketService.off('error', handleError);
    };
  }, [eventType]);

  return { data, loading, error };
}

export default useWebSocket;